import { apiUrl } from "../../utils/api";
import type { SchadenmappeData, MappeCostRow, MappeRequiredDocument, MappeDamage, DamagePatch } from "./types";

export async function getTodosByDamage(damageId: number): Promise<import('./types').MappeTodo[]> {
  const res = await fetch(apiUrl(`todo/by-linked/SCHADEN/${damageId}`));
  if (!res.ok) throw new Error(`Failed to load damage todos: ${res.status}`);
  return res.json();
}

export async function getSchadenmappe(id: number): Promise<SchadenmappeData> {
  const mappeRes = await fetch(apiUrl(`damage/${id}/mappe`));
  if (!mappeRes.ok) throw new Error(`Failed to load Schadenmappe: ${mappeRes.status}`);
  const base = await mappeRes.json() as Omit<SchadenmappeData, "todos"> & { todos?: SchadenmappeData["todos"] };

  const todos = await getTodosByDamage(id);
  return { ...base, todos };
}

/** Legt einen Schaden an (Status „O“); die Antwort enthält Schadennummer und Vorgang. */
export async function createDamage(body: {
  contact: { id: number };
  riskObject?: { id: number } | null;
  contract?: { id: number } | null;
  meldung: Record<string, unknown>;
  description?: string;
}): Promise<MappeDamage> {
  const res = await fetch(apiUrl("damage"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    // Steuersatz ausdrücklich mitsenden: Der Decimal-Transformer der Entity macht
    // aus „nicht gesetzt“ ein NULL und hebelt so den DB-Standard (19) aus → 500.
    body: JSON.stringify({ versSteuerPct: 19, ...body, status: "O" }),
  });
  if (!res.ok) throw new Error(`Failed to create damage: ${res.status}`);
  return res.json();
}

/** Speichert einen Teil-Patch und liefert den gespeicherten Schaden zurück. */
export async function updateDamage(id: number, data: DamagePatch): Promise<MappeDamage> {
  const res = await fetch(apiUrl(`damage/${id}`), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Failed to update damage: ${res.status}`);
  return res.json();
}

/** Stellt sicher, dass der Schaden einen Schadenvorgang hat. */
export async function ensureVorgang(id: number): Promise<{ vorgangId: number }> {
  const res = await fetch(apiUrl(`damage/${id}/vorgang`), { method: "POST" });
  if (!res.ok) throw new Error(`Failed to ensure Vorgang: ${res.status}`);
  return res.json();
}

export async function createCostRow(damageId: number, data: Partial<MappeCostRow>): Promise<MappeCostRow> {
  const res = await fetch(apiUrl(`damage/${damageId}/cost-rows`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    // Zahlenspalten ausdrücklich mitsenden: Der Decimal-Transformer der Entity
    // macht aus „nicht gesetzt“ ein NULL und hebelt die DB-Standards aus → 500.
    body: JSON.stringify({ taxRate: 19, invoiceNet: 0, deductionGross: 0, paymentGross: 0, ...data }),
  });
  if (!res.ok) throw new Error(`Failed to create cost row: ${res.status}`);
  return res.json();
}

export async function updateCostRow(damageId: number, rowId: number, data: Partial<MappeCostRow>): Promise<MappeCostRow> {
  const res = await fetch(apiUrl(`damage/${damageId}/cost-rows/${rowId}`), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Failed to update cost row: ${res.status}`);
  return res.json();
}

export async function deleteCostRow(damageId: number, rowId: number): Promise<void> {
  const res = await fetch(apiUrl(`damage/${damageId}/cost-rows/${rowId}`), { method: "DELETE" });
  if (!res.ok) throw new Error(`Failed to delete cost row: ${res.status}`);
}

export async function updateRequiredDocument(damageId: number, docId: number, data: Partial<MappeRequiredDocument>): Promise<MappeRequiredDocument> {
  const res = await fetch(apiUrl(`damage/${damageId}/required-documents/${docId}`), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Failed to update required document: ${res.status}`);
  return res.json();
}

export async function createRequiredDocument(damageId: number, data: Partial<MappeRequiredDocument>): Promise<MappeRequiredDocument> {
  const res = await fetch(apiUrl(`damage/${damageId}/required-documents`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Failed to create required document: ${res.status}`);
  return res.json();
}
